import { RagChatToggleService } from '../../service/rag-chat-toggle.service';
import {
  RagChatService,
  RagSource,
  RagStreamEvent,
  Scope,
} from '../../service/rag-chat.service';
import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BarComponent,
  BarElementDirective,
  BarLeftDirective,
  BarRightDirective,
} from '@fundamental-ngx/core/bar';
import { BusyIndicatorComponent } from '@fundamental-ngx/core/busy-indicator';
import { ButtonComponent } from '@fundamental-ngx/core/button';
import { IconComponent } from '@fundamental-ngx/core/icon';
import { ScrollbarDirective } from '@fundamental-ngx/core/scrollbar';
import { TitleComponent } from '@fundamental-ngx/core/title';
import { MarkdownComponent, provideMarkdown } from 'ngx-markdown';
import { EMPTY, Subject, map, merge, switchMap } from 'rxjs';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: RagSource[];
  loading?: boolean;
}

interface ScopeOption {
  value: Scope;
  label: string;
  description: string;
}

@Component({
  selector: 'app-rag-chat',
  imports: [
    DecimalPipe,
    MarkdownComponent,
    ButtonComponent,
    BarComponent,
    BarElementDirective,
    BarLeftDirective,
    BarRightDirective,
    IconComponent,
    TitleComponent,
    BusyIndicatorComponent,
    ScrollbarDirective,
  ],
  templateUrl: './rag-chat.component.html',
  styleUrl: './rag-chat.component.scss',
  providers: [provideMarkdown()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RagChatComponent {
  private messagesContainer =
    viewChild<ElementRef<HTMLElement>>('messagesContainer');

  private toggleService = inject(RagChatToggleService);
  private ragService = inject(RagChatService);
  private query$ = new Subject<{
    question: string;
    scope: Scope;
  }>();
  private cancel$ = new Subject<void>();

  readonly scopeOptions: ScopeOption[] = [
    {
      value: 'external',
      label: 'External',
      description: 'Public documentation',
    },
    {
      value: 'internal',
      label: 'Internal',
      description: 'Internal documentation',
    },
    { value: 'all', label: 'All', description: 'All documentation' },
  ];

  visible = this.toggleService.visible;
  expanded = signal(false);
  messages = signal<ChatMessage[]>([]);
  inputText = signal('');
  isStreaming = signal(false);
  scope = signal<Scope>('external');
  scopeMenuOpen = signal(false);

  constructor() {
    merge(this.query$, this.cancel$.pipe(map(() => null)))
      .pipe(
        switchMap((req) =>
          req ? this.ragService.queryStream(req.question, req.scope) : EMPTY,
        ),
        takeUntilDestroyed(),
      )
      .subscribe((event: RagStreamEvent) => {
        this.handleStreamEvent(event);
      });
  }

  close(): void {
    this.cancel$.next();
    this.isStreaming.set(false);
    this.messages.update((msgs) => {
      if (!msgs.length) return msgs;
      const last = msgs[msgs.length - 1];
      if (last.role === 'assistant' && last.loading) {
        return msgs.slice(0, -1);
      }
      return msgs;
    });
    this.toggleService.close();
  }

  toggleExpand(): void {
    this.expanded.update((v) => !v);
  }

  toggleScopeMenu(): void {
    this.scopeMenuOpen.update((v) => !v);
  }

  setScope(value: Scope): void {
    this.scope.set(value);
    this.scopeMenuOpen.set(false);
  }

  scopeLabel(): string {
    return this.scopeOptions.find((o) => o.value === this.scope())!.label;
  }

  send(): void {
    const question = this.inputText().trim();
    if (!question || this.isStreaming()) return;

    this.messages.update((msgs) => [
      ...msgs,
      { role: 'user', content: question },
      { role: 'assistant', content: '', loading: true },
    ]);
    this.clearInput();
    this.isStreaming.set(true);

    this.query$.next({ question, scope: this.scope() });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  onInputChange(event: Event): void {
    this.inputText.set((event.target as HTMLTextAreaElement).value);
  }

  private handleStreamEvent(event: RagStreamEvent): void {
    switch (event.type) {
      case 'metadata':
        this.messages.update((msgs) => {
          const updated = [...msgs];
          const last = updated.at(-1);
          if (!last || last.role !== 'assistant') return msgs;
          updated[updated.length - 1] = { ...last, sources: event.sources };
          return updated;
        });
        break;

      case 'chunk':
        this.messages.update((msgs) => {
          const updated = [...msgs];
          const last = updated.at(-1);
          if (!last || last.role !== 'assistant') return msgs;
          updated[updated.length - 1] = {
            ...last,
            content: last.content + event.content,
            loading: false,
          };
          return updated;
        });
        this.scrollToBottom();
        break;

      case 'done':
        this.isStreaming.set(false);
        break;

      case 'error':
        this.messages.update((msgs) => {
          const updated = [...msgs];
          const last = updated.at(-1);
          if (!last || last.role !== 'assistant') return msgs;
          updated[updated.length - 1] = {
            ...last,
            content: 'Something went wrong. Please try again later.',
            loading: false,
          };
          return updated;
        });
        this.isStreaming.set(false);
        break;
    }
  }

  private clearInput(): void {
    this.inputText.set('');
  }

  private scrollToBottom(): void {
    const el = this.messagesContainer()?.nativeElement;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }
}
